// The unsaved edits of a Vault's configuration survive leaving the page and coming Back (the browser's
// Back must return to the same view): the draft is kept in sessionStorage per Vault. Secret values are
// never stored; they are the one thing that is lost, which is why leaving with one asks first.
import type { Edits } from './vaultconfig.ts';

export type DraftSource = 'off' | 'acme' | 'pem' | 'pkcs12';
export interface Draft { edits: Edits; picked: DraftSource | null }

const keyOf = (vaultId: string) => `mv.configDraft.${vaultId}`;

export function readDraft(vaultId: string): Draft {
  try {
    const raw = window.sessionStorage.getItem(keyOf(vaultId));
    if (raw) {
      const d = JSON.parse(raw) as Draft;
      if (d && typeof d.edits === 'object' && d.edits) return { edits: d.edits, picked: d.picked ?? null };
    }
  } catch { /* no storage, or damaged: no draft */ }
  return { edits: {}, picked: null };
}

/** Edits without the secret ones; what may be stored. */
export function withoutSecrets(edits: Edits, secretKeys: ReadonlySet<string>): Edits {
  return Object.fromEntries(Object.entries(edits).filter(([k]) => !secretKeys.has(k)));
}

export function writeDraft(vaultId: string, edits: Edits, picked: DraftSource | null, secretKeys: ReadonlySet<string>): void {
  try {
    const safe = withoutSecrets(edits, secretKeys);
    if (Object.keys(safe).length === 0 && !picked) window.sessionStorage.removeItem(keyOf(vaultId));
    else window.sessionStorage.setItem(keyOf(vaultId), JSON.stringify({ edits: safe, picked }));
  } catch { /* the draft is a convenience */ }
}

export const clearDraft = (vaultId: string): void => writeDraft(vaultId, {}, null, new Set());
